import {PDFDocument, PDFPage, StandardFonts, degrees, rgb} from 'pdf-lib'
import moment from 'moment'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'

const VOS_FORM_V2_PATH = '/media/other/vos_form_v2.pdf'

/** Value baselines measured from vos_form_v2.pdf labels (pdf-lib bottom origin). */
const PAGE1_VALUE_Y = {
  assignmentId: 709.5,
  dateTime: 709.8,
  interpreter: 687.8,
  client: 666,
  language: 644.3,
  duration: 622.5,
  address: 600.8,
  instructions: 579,
  consumer: 558,
  breakTime: 343.8,
} as const

const PAGE3_HEADER_VALUE_Y = {
  assignmentId: 603.4,
  dateTime: 604.2,
  interpreter: 581.7,
  client: 559.9,
  language: 538.2,
  duration: 516.4,
  address: 494.6,
  instructions: 472.6,
  consumer: 451.6,
} as const

const VALUE_X = 195
const DATE_TIME_CENTER_X = 475

export type VosFormV2Appointment = {
  code?: number | string
  startTime?: string
  endTime?: string
  confirmedInterpreterName?: string
  customerName?: string
  requesterName?: string
  languageTo?: string
  address?: string
  description?: string
  consumer?: string
  communicationTypeIdentification?: string
  breakTimeMinutes?: number | string | null
}

function sanitizeTextForPDF(text: string): string {
  if (!text) return ''
  let sanitized = text.replace(/[\u200B-\u200D\u202A-\u202E\u2060-\u206F\uFEFF]/g, '')
  sanitized = sanitized
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u00A0]/g, ' ')
  return sanitized.replace(/[^\x20-\x7E\xA0-\xFF]/g, '')
}

function calculateDuration(start?: string, end?: string): string {
  if (!start || !end) return ''
  const mStart = moment(start)
  const mEnd = moment(end)
  if (!mStart.isValid() || !mEnd.isValid()) return ''
  const diff = moment.duration(mEnd.diff(mStart))
  return `${Math.floor(diff.asHours())} hours ${diff.minutes()} minutes`
}

function wrapText(
  text: string,
  measure: (line: string) => number,
  maxWidth: number
): string[] {
  if (!text) return []
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    if (measure(testLine) > maxWidth && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  }
  if (currentLine) lines.push(currentLine)
  return lines
}

type DrawContext = {
  helvetica: Awaited<ReturnType<PDFDocument['embedFont']>>
  helveticaBold: Awaited<ReturnType<PDFDocument['embedFont']>>
  labelColor: ReturnType<typeof rgb>
}

type FieldBundle = {
  appointmentId: string
  dateTime: string
  dateTimeWidth: number
  interpreterName: string
  customerName: string
  language: string
  duration: string
  address: string
  descriptionLines: string[]
}

function drawVosFormHeaderFields(
  targetPage: PDFPage,
  fields: FieldBundle,
  ctx: DrawContext,
  valueY: typeof PAGE1_VALUE_Y | typeof PAGE3_HEADER_VALUE_Y
) {
  const fontSize = 10
  const draw = (text: string, y: number) => {
    if (!text) return
    targetPage.drawText(text, {
      x: VALUE_X,
      y,
      size: fontSize,
      font: ctx.helvetica,
      color: rgb(0, 0, 0),
    })
  }

  draw(fields.appointmentId, valueY.assignmentId)
  draw(fields.interpreterName, valueY.interpreter)
  draw(fields.customerName, valueY.client)
  draw(fields.language, valueY.language)
  draw(fields.duration, valueY.duration)
  draw(fields.address, valueY.address)

  if (fields.dateTime) {
    targetPage.drawText(fields.dateTime, {
      x: DATE_TIME_CENTER_X - fields.dateTimeWidth / 2,
      y: valueY.dateTime,
      size: fontSize,
      font: ctx.helvetica,
      color: rgb(0, 0, 0),
    })
  }

  const instrFontSize = 9
  const lineHeight = 10
  let currentY = valueY.instructions
  for (const line of fields.descriptionLines) {
    targetPage.drawText(line, {
      x: VALUE_X,
      y: currentY,
      size: instrFontSize,
      font: ctx.helvetica,
      color: rgb(0, 0, 0),
    })
    currentY -= lineHeight
  }
}

function drawConsumerField(
  targetPage: PDFPage,
  yPos: number,
  consumerName: string,
  ctx: DrawContext
) {
  targetPage.drawText('Consumer:', {
    x: 90,
    y: yPos,
    size: 10,
    font: ctx.helveticaBold,
    color: ctx.labelColor,
  })
  targetPage.drawText(consumerName, {
    x: 195,
    y: yPos,
    size: 10,
    font: ctx.helvetica,
    color: rgb(40 / 255, 39 / 255, 39 / 255),
  })
}

function drawPage1BreakTimeValue(
  targetPage: PDFPage,
  breakTimeMinutes: string,
  ctx: DrawContext
) {
  if (!breakTimeMinutes) return
  const label = 'Break time (if applicable):'
  const fontSize = 10
  const labelWidth = ctx.helveticaBold.widthOfTextAtSize(label, fontSize)
  targetPage.drawText(breakTimeMinutes, {
    x: 72 + labelWidth + 4,
    y: PAGE1_VALUE_Y.breakTime,
    size: fontSize,
    font: ctx.helvetica,
    color: rgb(0, 0, 0),
  })
}

function applyWatermark(pages: PDFPage[], text: string, helvetica: DrawContext['helvetica']) {
  const wmSize = 72
  const wmWidth = helvetica.widthOfTextAtSize(text, wmSize)
  pages.forEach((targetPage) => {
    const {width, height} = targetPage.getSize()
    targetPage.drawText(text, {
      x: width / 2 - wmWidth / 2 - 40,
      y: height / 2 - 80,
      size: wmSize,
      font: helvetica,
      color: rgb(0.85, 0.1, 0.1),
      opacity: 0.25,
      rotate: degrees(30),
    })
  })
}

/** Fill values onto `/media/other/vos_form_v2.pdf` — the official 3-page template. */
export async function buildFilledVosFormV2Pdf(
  appointment: VosFormV2Appointment,
  watermarkText?: string
): Promise<Uint8Array> {
  const templateUrl = toAbsoluteUrl(VOS_FORM_V2_PATH)
  const response = await fetch(templateUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch VOS template: ${response.statusText}`)
  }

  const pdfDoc = await PDFDocument.load(await response.arrayBuffer())
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
  const ctx: DrawContext = {
    helvetica,
    helveticaBold,
    labelColor: rgb(0.35, 0.35, 0.35),
  }

  const pages = pdfDoc.getPages()

  const appointmentId = sanitizeTextForPDF(String(appointment.code ?? ''))
  const dateTime = sanitizeTextForPDF(
    appointment.startTime ? moment(appointment.startTime).format('YYYY-MM-DD HH:mm') : ''
  )
  const dateTimeWidth = helvetica.widthOfTextAtSize(dateTime, 10)
  const interpreterName = sanitizeTextForPDF(`${appointment.confirmedInterpreterName ?? ''}`.trim())
  const customerName = sanitizeTextForPDF(
    `${appointment.customerName ?? ''} - ${appointment.requesterName ?? ''}`.trim()
  )
  const language = sanitizeTextForPDF(`${appointment.languageTo ?? ''}`)
  const duration = sanitizeTextForPDF(
    calculateDuration(appointment.startTime, appointment.endTime)
  )
  const address = sanitizeTextForPDF(appointment.address ?? '')
  const descriptionApp = sanitizeTextForPDF(appointment.description ?? '')
  const consumerName = sanitizeTextForPDF((appointment.consumer ?? '').trim())

  const instrFontSize = 9
  const lineHeight = 10
  let descriptionLines = wrapText(
    descriptionApp,
    (line) => helvetica.widthOfTextAtSize(line, instrFontSize),
    390
  )
  const minInstructionsY = PAGE1_VALUE_Y.consumer + 11
  const availableLines =
    Math.floor((PAGE1_VALUE_Y.instructions - minInstructionsY) / lineHeight) + 1
  if (descriptionLines.length > availableLines) {
    descriptionLines = descriptionLines.slice(0, availableLines)
    const lastLine = descriptionLines[availableLines - 1]
    if (lastLine.length > 3) {
      descriptionLines[availableLines - 1] = lastLine.substring(0, lastLine.length - 3) + '...'
    }
  }

  const fieldBundle: FieldBundle = {
    appointmentId,
    dateTime,
    dateTimeWidth,
    interpreterName,
    customerName,
    language,
    duration,
    address,
    descriptionLines,
  }

  drawVosFormHeaderFields(pages[0], fieldBundle, ctx, PAGE1_VALUE_Y)
  drawConsumerField(pages[0], PAGE1_VALUE_Y.consumer, consumerName, ctx)

  const breakTimeValue =
    appointment.breakTimeMinutes != null && appointment.breakTimeMinutes !== ''
      ? sanitizeTextForPDF(String(appointment.breakTimeMinutes))
      : ''
  drawPage1BreakTimeValue(pages[0], breakTimeValue, ctx)

  const isThirdParty = appointment.communicationTypeIdentification === 'THIRD_PART_PLATFORM'
  if (isThirdParty && pages.length >= 3) {
    drawVosFormHeaderFields(pages[pages.length - 1], fieldBundle, ctx, PAGE3_HEADER_VALUE_Y)
    drawConsumerField(pages[pages.length - 1], PAGE3_HEADER_VALUE_Y.consumer, consumerName, ctx)
  }

  if (watermarkText) {
    applyWatermark(pages, watermarkText, helvetica)
  }

  return pdfDoc.save()
}
